/**
 * The option surfaces (surfaces.md §2.2): `choice · one`, `choice · many`,
 * `order`. Keyboard-first: a digit picks the option in that place, read **by
 * code** (`Digit1`…`Digit9`, `Numpad1`…), so the Lithuanian layout's `ą` on
 * `Digit1` still picks the first option. A click does the same and is traced
 * as `Mouse0` on `opt:<id>`. Leaves are option ids, stable per (question, rev).
 */
import { type KeyLike, digitOfCode, isBrowserChord, isInert } from "./keys";
import type { Action, Entry } from "./slots";

export interface OptState {
  /** choice: picked ids in pick order; order: the sequence built so far. */
  picked: string[];
}

export type OptKind = "one" | "many" | "order";

export function optKind(id: string, mode: string): OptKind | null {
  if (id === "choice") return mode === "many" ? "many" : "one";
  if (id === "order") return "order";
  return null;
}

export function optInput(kind: OptKind, st: OptState): Record<string, unknown> {
  return kind === "order" ? { seq: [...st.picked] } : { picked: [...st.picked] };
}

function toggle(kind: OptKind, st: OptState, id: string): [OptState, string] {
  const has = st.picked.includes(id);
  if (kind === "one") return [{ picked: [id] }, "pick"];
  if (kind === "many") return has ? [{ picked: st.picked.filter((x) => x !== id) }, "unpick"] : [{ picked: [...st.picked, id] }, "pick"];
  // order: a card already in the line comes out and the numbers close up
  return has ? [{ picked: st.picked.filter((x) => x !== id) }, "unpick"] : [{ picked: [...st.picked, id] }, "pick"];
}

export interface OptStep {
  state: OptState;
  entry: Entry | null;
  action: Action;
  native?: boolean;
}

export function optKey(kind: OptKind, options: { id: string }[], st: OptState, k: KeyLike, test: boolean): OptStep {
  const e = (op: string, x?: string): Entry => (x === undefined ? [k.code, k.key, "", op] : [k.code, k.key, "", op, x]);
  if (k.key === "Enter") {
    if (k.repeat) return { state: st, entry: e("repeat"), action: null };
    if ((k.ctrlKey || k.metaKey) && test) return { state: st, entry: e("skip"), action: "skip" };
    if (st.picked.length === 0) return { state: st, entry: e("enter_empty"), action: null };
    return { state: st, entry: e("enter"), action: "commit" };
  }
  if (isInert(k) || isBrowserChord(k) || k.key === "Tab") return { state: st, entry: null, action: null, native: true };
  if (k.key === "Backspace") {
    if (st.picked.length === 0) return { state: st, entry: e("bksp", "empty"), action: null };
    const gone = st.picked[st.picked.length - 1];
    return { state: { picked: kind === "one" ? [] : st.picked.slice(0, -1) }, entry: e("unpick", gone), action: null };
  }
  const d = digitOfCode(k.code);
  if (d !== null && d !== "0") {
    const opt = options[Number(d) - 1];
    if (!opt) return { state: st, entry: e("refuse", "no_option"), action: null };
    if (kind === "order" && st.picked.includes(opt.id)) return { state: st, entry: e("refuse", "placed"), action: null };
    const [n, op] = toggle(kind, st, opt.id);
    return { state: n, entry: e(op, opt.id), action: null };
  }
  return { state: st, entry: e("refuse", "not_in_slot"), action: null };
}

export function optClick(kind: OptKind, st: OptState, id: string): OptStep {
  const [n, op] = toggle(kind, st, id);
  return { state: n, entry: ["Mouse0", `opt:${id}`, "", op, id], action: null };
}
